import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ExternalLink, FlaskConical, CheckCircle2 } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const Videnskab = () => {
  const { t } = useTranslation();
  const showItems = t("videnskab.show_items", { returnObjects: true });
  const studies = t("videnskab.studies", { returnObjects: true });

  return (
    <section
      id="videnskab"
      data-testid="videnskab-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t("videnskab.eyebrow")}</span>
            <h2 className="font-display mt-5 text-lg sm:text-xl lg:text-[1.25rem] font-semibold leading-[1.25] text-white glow-text-white uppercase tracking-[-0.005em]">
              {t("videnskab.title")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
              {t("videnskab.lead")}
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="glass-strong rounded-2xl p-8 electric-border">
              <div className="flex items-center gap-3">
                <FlaskConical className="h-5 w-5 text-[#6FF6FF]" />
                <span className="eyebrow">{t("videnskab.show_title")}</span>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {Array.isArray(showItems) &&
                  showItems.map((it, i) => (
                    <li
                      key={i}
                      data-testid={`videnskab-finding-${i}`}
                      className="flex items-start gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-sm text-white/90"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3DCB]" />
                      <span>{it}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Studies grid */}
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {Array.isArray(studies) &&
            studies.map((s, i) => (
              <motion.a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`videnskab-study-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="group relative flex flex-col rounded-2xl glass p-6 electric-border transition-transform hover:-translate-y-1"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#6FF6FF]">
                  {t("videnskab.study_label")} · 0{i + 1}
                </span>
                <h3 className="font-display mt-3 text-lg font-semibold leading-snug text-white">
                  {s.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/65">
                  {s.body}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#FF3DCB] transition-colors group-hover:text-white">
                  {t("videnskab.read_study")}
                  <ExternalLink className="h-3 w-3" />
                </span>
              </motion.a>
            ))}
        </div>
      </div>
    </section>
  );
};

export default Videnskab;
